import { ChallengeState, calculateTimeLeft } from "@ascendnow/shared";
import classNames from "classnames";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import duration from "dayjs/plugin/duration";

import { pluralizeIfNecessary } from "@ascendnow/shared";
import AscButton from "../../AscButton/AscButton";
import {
  CalendarEmptyIcon,
  CheckActiveIcon,
  ClockIcon,
  CrossActiveIcon,
  LockIcon,
  PendingActiveIcon,
} from "../../AscIcons";
import AscGenericCard from "../AscGenericCard/AscGenericCard";
import styles from "./AscChallengeCard.module.scss";
import AscLink from "../../AscLink/AscLink";

dayjs.extend(relativeTime);
dayjs.extend(duration);

export interface AscChallengeCardProps {
  title: string;
  challengePath: string;
  coverImageUrl?: string;
  state: ChallengeState;
  completedDate?: string;
  estimatedTimeInHours?: number;
  coins?: number;
  points?: number;
  deadlineDate?: string;
  className?: string;
  mainChallenge?: boolean;
  mainChallengeLocked?: boolean;
  loading?: boolean;
  linkWrapperClassName?: string;
  hasNewFeedback?: boolean;
  feedbackCount?: number;
  onViewFeedback?: () => void;
}

export default function AscChallengeCard({
  title,
  coverImageUrl,
  state = "not-started",
  completedDate,
  deadlineDate,
  estimatedTimeInHours,
  points,
  coins,
  className,
  mainChallenge = false,
  mainChallengeLocked = false,
  challengePath,
  loading = false,
  hasNewFeedback = false,
  feedbackCount = 0,
  linkWrapperClassName,
  onViewFeedback,
}: AscChallengeCardProps) {
  const remainingTimeInHours = dayjs(deadlineDate).diff(dayjs(), "hours", true);
  const remainingTime = calculateTimeLeft(deadlineDate);
  const completedPercentage =
    (((estimatedTimeInHours || 0) - remainingTimeInHours) /
      (estimatedTimeInHours || 1)) *
    100;
  const progressBarStyle = {
    "--value": completedPercentage,
  } as React.CSSProperties;

  const getFooterContent = (eventType: ChallengeState) => {
    if (eventType === "not-started")
      return (
        <AscLink
          href={challengePath}
          innerWrapperTag={null}
          className={styles.actionButtonLink}
        >
          <AscButton
            fullWidth
            variant={mainChallengeLocked ? "white" : "primary"}
            disabled={mainChallengeLocked}
          >
            Explore
          </AscButton>
        </AscLink>
      );
    else if (eventType === "completed" && feedbackCount > 0)
      return (
        <div className={styles.viewFeedbackContainer}>
          <AscButton
            className={styles.viewFeedback}
            size="small"
            fullWidth
            onClick={onViewFeedback}
          >
            View Feedback
          </AscButton>
          {hasNewFeedback && (
            <span className={styles.viewFeedbackBadge}>New</span>
          )}
        </div>
      );
    else if (eventType === "wrong")
      return (
        <AscLink
          href={challengePath}
          innerWrapperTag={null}
          className={styles.actionButtonLink}
        >
          <AscButton fullWidth size="small">
            Try Again
          </AscButton>
        </AscLink>
      );
    else if (eventType === "pending")
      return (
        <AscButton className={styles.pendingButton} size="small" fullWidth>
          Pending
        </AscButton>
      );
    else return <></>;
  };

  const getContent = (eventType: ChallengeState) => {
    switch (eventType) {
      case "not-started":
        return (
          <div className={styles.availableContent}>
            {mainChallengeLocked ? (
              <div className={styles.lockedIconWrapper}>
                <span className={styles.lockIconContainer}>
                  <LockIcon className={styles.lockIcon} />
                </span>
              </div>
            ) : null}
            <ClockIcon className={styles.clockIcon} />
            <span className={styles.time}>
              {estimatedTimeInHours
                ? dayjs.duration({ hours: estimatedTimeInHours }).humanize()
                : "No timeline"}
            </span>
          </div>
        );
      case "completed":
        return (
          <div className={styles.pastContent}>
            <div className={styles.completeIconWrapper}>
              <CheckActiveIcon
                data-testid={"asc-challenge-card-past-icon"}
                className={styles.completeIcon}
              />
            </div>
            <CalendarEmptyIcon className={styles.calendarIcon} />
            <span className={styles.time}>
              {dayjs(completedDate).format("D MMMM YYYY")}
            </span>
          </div>
        );
      case "in-progress":
        return (
          <div className={styles.currentContent}>
            {remainingTime && estimatedTimeInHours ? (
              <>
                <div
                  className={styles.progressBar}
                  role="progressbar"
                  aria-valuenow={completedPercentage}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  style={progressBarStyle}
                />
                <div className={styles.label}>
                  <span className={styles.time}>
                    {remainingTime?.value} {remainingTime?.unit}
                  </span>{" "}
                  left
                </div>
              </>
            ) : null}
          </div>
        );
      case "wrong":
        return (
          <div className={styles.errorContent}>
            <CrossActiveIcon
              data-testid={"asc-challenge-card-error-icon"}
              className={styles.errorIcon}
            />
          </div>
        );
      case "pending":
        return (
          <div className={styles.pendingContent}>
            <PendingActiveIcon
              data-testid={"asc-challenge-card-pending-icon"}
              className={styles.pendingIcon}
            />
          </div>
        );
    }
  };

  return (
    <AscLink
      href={challengePath}
      className={classNames(styles.linkWrapper, linkWrapperClassName)}
      innerWrapperTag={null}
      disabled={mainChallengeLocked}
    >
      <AscGenericCard
        loading={loading}
        className={classNames(
          styles.ground,
          {
            [styles.available]: state === "not-started",
            [styles.past]: state === "completed",
            [styles.current]: state === "in-progress",
            [styles.error]: state === "wrong",
            [styles.pending]: state === "pending",
            [styles.mainChallenge]: mainChallengeLocked,
          },
          className
        )}
        imageClassName={styles.cardImage}
        bodyClassName={styles.cardBody}
        titleClassName={styles.cardTitle}
        title={title}
        coverImageUrl={coverImageUrl}
        tags={[
          coins
            ? {
                label: `${coins} ${pluralizeIfNecessary("coin", coins)}`,
                type:
                  state === "completed"
                    ? "card-brand-red-inverted"
                    : "card-brand-red",
              }
            : undefined,
          points
            ? {
                label: `${points} ${pluralizeIfNecessary("point", points)}`,
                type:
                  state === "completed"
                    ? "card-light-green-inverted"
                    : "card-light-green",
              }
            : undefined,
        ]}
        tagsContainerClassName={styles.tagsContainer}
        footerClassName={styles.cardFooter}
        footer={getFooterContent(state)}
        contentClassName={styles.cardContent}
      >
        {getContent(state)}
      </AscGenericCard>
    </AscLink>
  );
}
